import React, { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Accessible modal wrapper shared by all dialogs.
 *
 * Provides dialog semantics (role, aria-modal), Escape-to-close,
 * focus trapping, focus restoration to the previously focused element,
 * and backdrop-click-to-close. Renders as a bottom sheet on mobile and
 * a centered card on larger screens (or always centered with `centered`).
 *
 * Set `dismissible={false}` for dialogs that require an explicit choice
 * (backdrop click and Escape are ignored).
 */
const Modal = ({
  onClose,
  labelledBy,
  centered = false,
  dismissible = true,
  panelClassName = '',
  children
}) => {
  const panelRef = useRef(null);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const panel = panelRef.current;
    const firstFocusable = panel?.querySelector(FOCUSABLE_SELECTOR);
    (firstFocusable || panel)?.focus();
    return () => {
      if (previousFocus && typeof previousFocus.focus === 'function') {
        previousFocus.focus();
      }
    };
  }, []);

  const handleDismiss = () => {
    if (dismissible && onClose) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      handleDismiss();
      return;
    }
    if (e.key !== 'Tab') return;

    const panel = panelRef.current;
    if (!panel) return;
    const focusable = Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR));
    if (focusable.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className={`fixed inset-0 bg-black/50 flex ${
        centered ? 'items-center p-4' : 'items-end sm:items-center'
      } justify-center z-50`}
      onClick={handleDismiss}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className={`bg-white ${
          centered ? 'rounded-2xl' : 'rounded-t-2xl sm:rounded-2xl'
        } max-w-md w-full sm:mx-4 focus:outline-none ${panelClassName}`}
      >
        {children}
      </div>
    </div>
  );
};

export default Modal;
